<template>
  <div class="group-page">
    <skill-header title="自主组题" @back="handleBack">
      <button class="record-button" type="button" @click="openRecords"><img src="@/assets/images/skillCenterQuestion/record.png" alt="组题记录"></button>
    </skill-header>

    <main class="group-content">
      <section class="group-card">
        <div class="form-section">
          <h3>名称</h3>
          <input v-model="title" type="text" placeholder="请输入组题名称">
        </div>

        <div class="form-section">
          <h3>学科 <span>（可多选）</span></h3>
          <div class="chip-list">
            <button type="button" :class="{ active: allSubjectSelected }" @click="toggleAllSubjects">全部</button>
            <button v-for="item in subjectList" :key="item.id || item.categoryId" type="button" :class="{ active: subjectSelected(item) }" @click="toggleSubject(item)">{{ nodeLabel(item) }}</button>
          </div>
        </div>

        <div class="form-section type-section">
          <h3>题型 <span>（可多选）</span></h3>
          <div class="chip-list type-list">
            <button type="button" :class="{ active: allTypeSelected }" @click="toggleAllTypes">全部</button>
            <button v-for="item in questionTypes" :key="item.value" type="button" :class="{ active: typeSelected(item) }" @click="toggleType(item)">{{ item.label }}</button>
          </div>
        </div>

        <div class="form-section">
          <h3>分类</h3>
          <div class="chip-list classification-list">
            <button v-for="item in classifications" :key="item.value" type="button" :class="{ active: classification === item.value }" @click="selectClassification(item.value)">{{ item.label }}</button>
          </div>
        </div>

        <div class="form-section">
          <h3>模式</h3>
          <div class="chip-list mode-list">
            <button v-for="item in modes" :key="item.value" type="button" :class="{ active: mode === item.value }" @click="mode = item.value">{{ item.label }}</button>
          </div>
        </div>

        <div class="form-section quantity-section">
          <h3>题量</h3>
          <div class="chip-list quantity-list">
            <button v-for="item in quantities" :key="item" type="button" :disabled="item > maxNum" :class="{ active: num === item && !customQuantity }" @click="selectQuantity(item)">{{ item }}</button>
            <input
              ref="customCountInput"
              v-model="customCountInput"
              type="text"
              inputmode="numeric"
              pattern="[0-9]*"
              class="custom-input"
              :class="{ active: customQuantity }"
              placeholder="自定义"
              aria-label="自定义题量"
              @focus="activateCustomQuantity"
              @input="normalizeCustomQuantity"
            >
            <span class="available-question-count" aria-live="polite">可选 {{ availableNum }} 题</span>
          </div>
        </div>
      </section>
    </main>

    <footer class="group-footer">
      <button type="button" :disabled="!canStartAnswer" @click="startAnswer">开始做题</button>
    </footer>
  </div>
</template>

<script>
import {
  getSkillCenterFullTree,
  getSkillCenterGroupQuestionCount,
  getTree
} from '@/api/subject/index.js'
import { closeSkillCenterWeb } from '@/utils/skillCenter'
import { Toast } from 'vant'
import SkillHeader from './SkillHeader.vue'
import {
  QUESTION_TYPES,
  collectLeafIds,
  nodeId,
  nodeCount,
  nodeLabel as getNodeLabel,
  parseQueryArray,
  readSkillCenterGroupRecords,
  skillCenterQuestionCategories,
  skillCenterTreeData,
  writeSkillCenterGroupRecords
} from './shared'

const MAX_GROUP_QUESTION_COUNT = 200

export default {
  name: 'SkillCenterQuestionGroup',
  components: { SkillHeader },
  data() {
    return {
      title: '',
      subjectList: [],
      selectedCategoryIds: [],
      questionTypes: QUESTION_TYPES,
      selectedTypes: [],
      classification: '',
      mode: '',
      quantities: [15, 30, 50, 100],
      num: 0,
      availableNum: 0,
      maxNum: 0,
      customQuantity: false,
      customCountInput: '',
      submitting: false,
      classifications: [
        { value: 'all', label: '全部' },
        { value: 'wrong', label: '做错的' },
        { value: 'favorite', label: '收藏的' },
        { value: 'undone', label: '未做的' }
      ],
      modes: [
        { value: 'practice', label: '练习模式' },
        { value: 'fast', label: '快刷模式' },
        { value: 'test', label: '测试模式' },
        { value: 'recite', label: '背题模式' }
      ]
    }
  },
  computed: {
    majorType() {
      return this.$route.query.majorType || ''
    },
    allSubjectIds() {
      return this.subjectList.map((node) => nodeId(node)).filter(Boolean)
    },
    allSubjectSelected() {
      return this.allSubjectIds.length > 0 && this.allSubjectIds.every((id) => this.selectedCategoryIds.includes(id))
    },
    allTypeSelected() {
      return this.questionTypes.length > 0 && this.questionTypes.every((item) => this.typeSelected(item))
    },
    canStartAnswer() {
      return !this.submitting && Boolean(
        this.title.trim() &&
        this.selectedCategoryIds.length &&
        this.selectedTypes.length &&
        this.classification &&
        this.mode &&
        (!this.customQuantity || Boolean(this.customCountInput)) &&
        this.num > 0
      )
    }
  },
  async created() {
    this.title = this.$route.query.title || ''
    this.selectedCategoryIds = parseQueryArray(this.$route.query.selectedCategoryIds)
    this.selectedTypes = parseQueryArray(this.$route.query.selectedTypes)
      .map(Number)
      .filter((item) => !Number.isNaN(item))
    this.classification = this.normalizeClassification(this.$route.query.classification || '')
    this.mode = this.$route.query.mode
      ? this.normalizePracticeMode(this.$route.query.mode)
      : ''
    if (this.$route.query.num) this.num = Number(this.$route.query.num) || 0
    this.customQuantity = String(this.$route.query.customQuantity) === '1'
    if (this.customQuantity && this.num > 0) this.customCountInput = String(this.num)
    await this.loadTree()
    this.migrateGroupRecords()
    this.refreshQuestionNum()
  },
  methods: {
    normalizeClassification(value) {
      const legacyValues = {
        '1': 'undone',
        '2': 'wrong',
        '3': 'favorite'
      }
      return legacyValues[String(value)] || value
    },
    normalizePracticeMode(value) {
      const legacyModes = { quick: 'fast', review: 'recite' }
      return legacyModes[value] || value
    },
    async loadTree() {
      if (String(this.$route.query.preview) === '1') {
        this.subjectList = [
          { id: '1', categoryId: '1', label: '心电图', children: [] },
          { id: '2', categoryId: '2', label: '影像诊断', children: [] },
          { id: '3', categoryId: '3', label: '心肺听诊', children: [] },
          { id: '4', categoryId: '4', label: '医德医风', children: [] },
          { id: '5', categoryId: '5', label: '病史采集', children: [] },
          { id: '6', categoryId: '6', label: '病例分析', children: [] }
        ]
        this.availableNum = 124
        this.maxNum = 100
        return
      }
      try {
        if (this.$route.query.skillCenter) {
          const res = await getSkillCenterFullTree()
          const data = skillCenterTreeData(res)
          this.subjectList = skillCenterQuestionCategories(
            data,
            this.majorType,
            this.$route.query.categoryOrganId
          )
        } else {
          const res = await getTree('', { majorType: this.majorType })
          const data = res && Array.isArray(res.data) ? res.data : []
          this.subjectList = data.length === 1 && data[0].children ? data[0].children : data
        }
      } catch (error) {
        this.subjectList = []
      }
    },
    subjectSelected(node) {
      return this.selectedCategoryIds.includes(nodeId(node))
    },
    nodeLabel(node) {
      return getNodeLabel(node)
    },
    async toggleSubject(node) {
      const id = nodeId(node)
      if (!id) return
      if (this.selectedCategoryIds.includes(id)) {
        this.selectedCategoryIds = this.selectedCategoryIds.filter((item) => item !== id)
      } else {
        this.selectedCategoryIds = [...this.selectedCategoryIds, id]
      }
      await this.refreshQuestionNum()
    },
    async toggleAllSubjects() {
      this.selectedCategoryIds = this.allSubjectSelected ? [] : [...this.allSubjectIds]
      await this.refreshQuestionNum()
    },
    migrateGroupRecords() {
      const records = readSkillCenterGroupRecords()
      let changed = false
      records.forEach((record) => {
        const selectedIds = new Set((record.categoryIds || []).map(String))
        if (!selectedIds.size) return
        const categoryIds = this.subjectList
          .filter((subject) => {
            const subjectId = nodeId(subject)
            return selectedIds.has(subjectId) || collectLeafIds(subject).some((id) => selectedIds.has(String(id)))
          })
          .map((subject) => nodeId(subject))
          .filter(Boolean)
        if (!categoryIds.length || (
          categoryIds.length === selectedIds.size &&
          categoryIds.every((id) => selectedIds.has(id))
        )) return
        record.categoryIds = categoryIds
        record.categoryId = categoryIds[0]
        changed = true
      })
      if (changed) writeSkillCenterGroupRecords(records)
    },
    typeValues(item) {
      return (item.values || [item.value]).map(Number)
    },
    typeSelected(item) {
      return this.typeValues(item).every((value) => this.selectedTypes.includes(value))
    },
    async toggleType(item) {
      const values = this.typeValues(item)
      if (this.typeSelected(item)) {
        this.selectedTypes = this.selectedTypes.filter((value) => !values.includes(value))
      } else {
        this.selectedTypes = [...new Set(this.selectedTypes.concat(values))]
      }
      await this.refreshQuestionNum()
    },
    async toggleAllTypes() {
      this.selectedTypes = this.allTypeSelected
        ? []
        : [...new Set(this.questionTypes.reduce((result, item) => result.concat(this.typeValues(item)), []))]
      await this.refreshQuestionNum()
    },
    async selectClassification(value) {
      this.classification = value
      await this.refreshQuestionNum()
    },
    buildParams() {
      const categoryIds = this.selectedCategoryIds.filter(Boolean)
      const questionTypes = this.selectedTypes.filter((value) => value !== 'all')
      if (!categoryIds.length || !questionTypes.length || !this.classification) return null
      const questionStatus = this.normalizeClassification(this.classification)
      return {
        categoryIds: categoryIds.join(','),
        questionTypes: questionTypes.join(','),
        questionStatus
      }
    },
    async refreshQuestionNum() {
      if (String(this.$route.query.preview) === '1') return
      if (String(this.$route.query.skillCenter) !== '1') {
        const selectedIds = new Set(this.selectedCategoryIds.map(String))
        const total = this.subjectList.reduce((sum, node) => {
          return selectedIds.has(nodeId(node)) ? sum + Number(nodeCount(node) || 0) : sum
        }, 0)
        this.availableNum = total || (selectedIds.size ? 1000 : 0)
        this.maxNum = Math.min(this.availableNum, 1000)
        if (!this.maxNum || this.num > this.maxNum) this.num = 0
        return
      }
      const params = this.buildParams()
      if (!params) {
        this.availableNum = 0
        this.maxNum = 0
        this.num = 0
        return
      }
      try {
        const res = await getSkillCenterGroupQuestionCount(params)
        const value = res && res.data !== undefined ? res.data : res
        this.availableNum = Math.max(0, Number(value) || 0)
        this.maxNum = Math.min(this.availableNum, MAX_GROUP_QUESTION_COUNT)
        if (!this.maxNum) {
          this.num = 0
        } else if (this.num > this.maxNum) {
          this.num = 0
        }
      } catch (error) {
        this.availableNum = 0
        this.maxNum = 0
        this.num = 0
      }
    },
    selectQuantity(value) {
      if (value > this.maxNum) return
      this.customQuantity = false
      this.num = value
    },
    activateCustomQuantity() {
      this.customQuantity = true
    },
    normalizeCustomQuantity() {
      this.customQuantity = true
      const value = String(this.customCountInput || '').replace(/\D/g, '')
      const normalizedValue = value.replace(/^0+/, '')
      const numericValue = Number(normalizedValue)
      this.customCountInput = numericValue > MAX_GROUP_QUESTION_COUNT
        ? String(MAX_GROUP_QUESTION_COUNT)
        : normalizedValue
      this.num = this.customCountInput ? Number(this.customCountInput) : 0
    },
    openRecords() {
      this.$router.push({
        path: '/skillCenter/group/records',
        query: {
          majorType: this.majorType,
          categoryOrganId: this.$route.query.categoryOrganId,
          organCategoryId: this.$route.query.organCategoryId,
          studyBankId: this.$route.query.studyBankId,
          skillCenter: 1,
          preview: this.$route.query.preview
        }
      })
    },
    async startAnswer() {
      if (!this.title.trim()) {
        Toast('请输入组题名称')
        return
      }
      if (!this.selectedCategoryIds.length) {
        Toast('请至少选择一个学科')
        return
      }
      if (!this.selectedTypes.length) {
        Toast('请至少选择一个题型')
        return
      }
      if (!this.classification) {
        Toast('请至少选择一个分类')
        return
      }
      if (!this.mode) {
        Toast('请选择答题模式')
        return
      }
      if (this.customQuantity && !this.customCountInput) {
        Toast('请输入正整数题量')
        return
      }
      if (this.num <= 0) {
        Toast(this.maxNum > 0 ? '请选择题量' : '当前条件暂无可用题目')
        return
      }
      if (this.num > MAX_GROUP_QUESTION_COUNT) {
        Toast(`单次最多可组${MAX_GROUP_QUESTION_COUNT}道题，请调整题量`)
        return
      }
      if (this.num > this.availableNum) {
        Toast(`当前选择最多可组${this.availableNum}道题，已选择${this.num}道题`)
        return
      }
      this.submitting = true
      try {
        const count = Math.min(Number(this.num), this.maxNum, MAX_GROUP_QUESTION_COUNT)
        const id = String(Date.now())
        const record = {
          id,
          studyPaperId: id,
          title: this.title.trim(),
          name: this.title.trim(),
          createTime: this.formatDate(new Date()),
          categoryId: this.selectedCategoryIds[0],
          categoryIds: [...this.selectedCategoryIds],
          questionTypes: [...this.selectedTypes],
          categories: this.classification === 'all'
            ? ['wrong', 'favorite', 'undone']
            : [this.normalizeClassification(this.classification)],
          classification: this.classification,
          mode: this.mode,
          count,
          questionNum: count,
          answerNum: 0,
          questionIds: [],
          answers: {},
          paperInitialized: false,
          majorType: this.majorType,
          categoryOrganId: this.$route.query.categoryOrganId || '',
          organCategoryId: this.$route.query.organCategoryId || ''
        }
        const records = readSkillCenterGroupRecords()
        records.unshift(record)
        writeSkillCenterGroupRecords(records)
        this.$router.push({
          path: '/skillCenter/subjectHomeWork',
          query: {
            categoryId: record.categoryId,
            categoryIds: JSON.stringify(record.categoryIds),
            source: 'group',
            majorType: this.majorType,
            categoryOrganId: record.categoryOrganId,
            organCategoryId: record.organCategoryId,
            practiceMode: this.mode,
            questionLimit: record.count,
            questionTypes: JSON.stringify(record.questionTypes),
            groupCategories: JSON.stringify(record.categories),
            groupRecordId: record.id,
            groupNew: 1,
            label: record.title,
            skillCenter: 1,
            from: '/skillCenter/group'
          }
        })
      } catch (error) {
        Toast(error && error.msg ? error.msg : '组题失败，请稍后重试')
      } finally {
        this.submitting = false
      }
    },
    formatDate(date) {
      const pad = (value) => String(value).padStart(2, '0')
      return `${date.getFullYear()}.${pad(date.getMonth() + 1)}.${pad(date.getDate())} ${pad(date.getHours())}:${pad(date.getMinutes())}`
    },
    handleBack() {
      closeSkillCenterWeb(this, 'skillCenterGroupQuestion')
    }
  }
}
</script>

<style lang="scss" scoped>
.group-page {
  min-height: 100vh;
  padding-bottom: 76px;
  background: #f7f7f8;
  color: #111111;
  font-family: -apple-system, BlinkMacSystemFont, "PingFang SC", "Microsoft YaHei", sans-serif;
  box-sizing: border-box;
}

button,
input {
  font: inherit;
}

.group-page ::v-deep .skill-header {
  background: #ffffff;
}

.record-button {
  padding: 0;
  border: 0;
  background: transparent;
}

.record-button img {
  display: block;
  width: 20px;
  height: 20px;
  object-fit: contain;
}

.group-content {
  padding: 26px 14px 15px;
}

.group-card {
  min-height: 600px;
  padding: 11px 14px;
  border-radius: 12px;
  background: #ffffff;
  box-sizing: border-box;
}

.form-section {
  margin-bottom: 24px;
}

.form-section:not(:first-child):not(.quantity-section) {
  margin-bottom: 26px;
}

.form-section:last-child {
  margin-bottom: 0;
}

.form-section h3 {
  margin: 0 0 8px;
  font-size: 14px;
  font-weight: 600;
  line-height: 20px;
}

.form-section h3 span {
  color: #969aa3;
  font-size: 12px;
  font-weight: 400;
}

.form-section > input {
  width: 100%;
  height: 36px;
  padding: 0 12px;
  border: 0;
  border-radius: 8px;
  outline: 0;
  background: #f6f6f7;
  color: #111111;
  font-size: 14px;
  box-sizing: border-box;
}

.chip-list {
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 8px;
}

.chip-list button {
  height: 26px;
  padding: 0 3px;
  overflow: hidden;
  border: 0;
  border-radius: 4px;
  background: #f7f7f7;
  color: #111111;
  font-size: 12px;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.chip-list button.active {
  background: #eaf1ff;
  color: #2468ff;
}

.type-list {
  display: grid;
  grid-auto-columns: 64px;
  grid-auto-flow: row;
  grid-template-rows: repeat(2, 26px);
  grid-template-columns: repeat(5, 64px);
  justify-content: start;
  overflow-x: auto;
  scrollbar-width: none;
  -webkit-overflow-scrolling: touch;
}

.type-list::-webkit-scrollbar {
  display: none;
}

.type-list button:first-child {
  width: 52px;
}

.chip-list button:disabled {
  color: #b4b7bd;
}

.custom-input {
  width: 100%;
  height: 26px;
  padding: 0 3px;
  border: 0;
  border-radius: 4px;
  outline: 0;
  background: #f7f7f7;
  color: #111111;
  font-size: 12px;
  text-align: center;
  box-sizing: border-box;
}

.custom-input.active {
  background: #eaf1ff;
  color: #2468ff;
}

.custom-input::placeholder {
  color: #111111;
  opacity: 1;
}

.custom-input.active::placeholder {
  color: #2468ff;
}

.available-question-count {
  align-self: center;
  color: #969aa3;
  font-size: 12px;
  line-height: 26px;
  white-space: nowrap;
}

.group-footer {
  position: fixed;
  right: 0;
  bottom: 0;
  left: 0;
  z-index: 10;
  display: flex;
  align-items: flex-start;
  height: 84px;
  padding: 12px 16px max(10px, env(safe-area-inset-bottom));
  background: #ffffff;
  box-sizing: border-box;
}

.group-footer button {
  width: 100%;
  height: 40px;
  border: 0;
  border-radius: 20px;
  background: #1d66fe;
  color: #ffffff;
  font-size: 14px;
}

.group-footer button:disabled {
  opacity: 0.5;
}

@media (min-width: 600px) {
  .group-page,
  .group-footer {
    right: auto;
    left: 50%;
    width: 375px;
    transform: translateX(-50%);
  }
}
</style>
